import React, { useMemo } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { Link } from 'react-router-dom'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { EmailConnectButton } from '../../../../components/EmailConnectButton/EmailConnectButton'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

const ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_MICROSOFT_ADMIN_CONSENT_LINK!

export type OutlookMailIntegrationRowProps = { useIntegrationRedirectUri?: boolean }

export const OutlookMailIntegrationRow: React.FC<OutlookMailIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()

  const hasOutlookMailIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_MAIL))
  }, [userData])

  const hasOtherMailIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.type === 'mail' && i.serviceId !== IntegrationServiceId.OUTLOOK_MAIL))
  }, [userData])

  const redirectState = useIntegrationRedirectUri ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations') : createState()

  const uninstallOutlookMail = () => {
    const user = userData
    const integration = user!.integrations!.find((i) => i.serviceId === IntegrationServiceId.OUTLOOK_MAIL)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: user!.id,
        integrationId: integration!.id,
      },
    })
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.OUTLOOK_MAIL} width={48} height={48} />
            </div>
            <div>
              <h6>Outlook Mail</h6>
              <div>
                Sync e-mails from Outlook. <br />
                It might be needed for your admin to approve the integration. For more info, click{' '}
                <Link className='zocks-text-gray-900 text-decoration-underline' to={ADMIN_CONSENT_LINK}>
                  here
                </Link>{' '}
                .
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            <div className='connect-button-container'>
              {hasOutlookMailIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallOutlookMail} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <EmailConnectButton
                  serviceId={IntegrationServiceId.OUTLOOK_MAIL}
                  redirectState={redirectState}
                  hasOtherMailIntegration={hasOtherMailIntegration}
                >
                  Connect
                </EmailConnectButton>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
